import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Clock3 } from 'lucide-react';
import { parseRichText } from '@/pages/courses/components/rich-text-renderer';
import {
  CALENDAR_EVENT_META,
  formatEventDateRange,
  type CalendarEventItem,
} from '../calendar-utils';

type EventViewDialogProps = {
  event: CalendarEventItem | null;
  onOpenChange: (open: boolean) => void;
};

export const EventViewDialog = ({ event, onOpenChange }: EventViewDialogProps) => {
  const descriptionValue =
    event && 'description' in event.source
      ? event.source.description
      : event?.description ?? '';

  return (
    <Dialog open={Boolean(event)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        {event && (
          <>
            <DialogHeader>
              <DialogTitle className="pr-8 text-xl">{event.title}</DialogTitle>
            </DialogHeader>

            <div className="space-y-5">
              <Badge
                variant="outline"
                className={cn('border', CALENDAR_EVENT_META[event.type].badgeClassName)}
              >
                {CALENDAR_EVENT_META[event.type].label}
              </Badge>

              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock3 className="h-4 w-4" />
                {formatEventDateRange(event.startDate, event.endDate)}
              </div>

              {descriptionValue?.trim() && (
                <div>
                  <h3 className="mb-2 text-sm font-medium text-foreground">Description</h3>
                  <div className="text-sm text-muted-foreground">
                    {parseRichText(descriptionValue)}
                  </div>
                </div>
              )}

              {'invited_users' in event.source && event.source.invited_users.length > 0 && (
                <div>
                  <Separator className="mb-4" />
                  <h3 className="mb-3 text-sm font-medium text-foreground">
                    Invited Users
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {event.source.invited_users.map((user) => (
                      <div
                        key={user.id}
                        className="flex max-w-full items-center gap-2 rounded-full border bg-muted/30 px-2 py-1 text-sm"
                      >
                        <Avatar className="h-6 w-6">
                          <AvatarImage src={user.user_image || ''} />
                          <AvatarFallback>{user.name.slice(0, 1)}</AvatarFallback>
                        </Avatar>
                        <span className="max-w-40 truncate">{user.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
